import { Fragment } from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";

import useResponsiveWithHydrationFix from "@hooks/useResponsiveWithHydrationFix";
import useDeviceDetect from "@hooks/useDeviceDetect";

import { isBrowser } from "@helpers/browser";
import { cn } from "@helpers/general";

import { ALVideo } from "@components/ALComponents";
import ALShopifyImage from "@components/al_components/ALShopifyImage";
import DesktopImage from "@components/gallery/imagesSwiper/DesktopImage";
import EmergencyDaily from "@components/ProductPage/productSummary/EmergencyDaily";
import USPFakeImage from "@components/gallery/USPFakeImage";
import { ComponentType } from "@constants/ComponentType";
import "@components/gallery/imagesSwiper/styles.scss";
import IconArrow from "@svgs/arrow-br.svg";

const INDEX_USPS = 3;

function ImagesSwiper({
  images,
  hasVideo,
  video,
  componentType,
  showEmergency,
  product,
  showPreOrderFomo,
  onSlideClick,
  onSwiper,
  onActiveIndexChange,
  showUSPImage,
}) {
  const { isMobile } = useDeviceDetect();

  const showDesktopImage =
    !isMobile && (componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS);

  const customPDPNextClass = "custom-pdp-next";
  const customPDPPrevClass = "custom-pdp-prev";

  const customBYOBNextClass = `custom-byob-next-${product.handle}`;
  const customBYOBPrevClass = `custom-byob-prev-${product.handle}`;

  let navigation = false;
  if (componentType === ComponentType.CP_BYOB) {
    navigation = { nextEl: `.${customBYOBNextClass}`, prevEl: `.${customBYOBPrevClass}` };
  } else if (componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS) {
    navigation = { nextEl: `.${customPDPNextClass}`, prevEl: `.${customPDPPrevClass}` };
  }

  const paginationSettings = {
    clickable: true,
    bulletClass: "al-bullet",
    bulletActiveClass: "al-bullet-active",
    renderBullet: (index, className) => `<span class="${className}">&nbsp;</span>`,
  };

  const pagination = useResponsiveWithHydrationFix(
    componentType === ComponentType.PRODUCT_CAROUSEL ? false : paginationSettings,
    false
  );

  const showCustomNavigationButtons = useResponsiveWithHydrationFix(
    componentType === ComponentType.CP_BYOB,
    componentType === ComponentType.CP_BYOB ||
      componentType === ComponentType.PDP ||
      componentType === ComponentType.PDP_ADS
  );

  // Only wire up navigation selectors once the buttons are actually in the DOM.
  // Passing selector refs to Swiper before the elements exist causes the A11y/Navigation
  // modules to call removeEventListener on undefined, producing a blank-screen crash.
  const activeNavigation = showCustomNavigationButtons ? navigation : false;

  const slidesPerView = useResponsiveWithHydrationFix(
    componentType === ComponentType.PDP_ADS ? 1.1 : 1,
    1
  );

  return (
    <>
      <Swiper
        a11y={false}
        spaceBetween={0}
        slidesPerView={slidesPerView}
        loop
        onSwiper={onSwiper}
        onActiveIndexChange={onActiveIndexChange}
        pagination={pagination}
        navigation={activeNavigation}
        className={`images_swiper relative w-full ${
          componentType === ComponentType.PDP_ADS ? "aspect-[0.86]" : "aspect-[0.8]"
        }`}
        data-testid="images_swiper"
      >
        {hasVideo && (
          <SwiperSlide key="product_video_pdp">
            <ALVideo
              videoUrl={video?.url || null}
              customClasses={{
                container:
                  "pdp_image flex items-center w-full h-full relative aspect-[0.8] after:content-[''] after:bg-transparent after:w-full after:h-full after:min-h-[1px] after:absolute after:top-0",
                image_thumbnail: "flex",
                skeleton_container: "pdp_sk_m",
                skeleton_image: "pdp_sk_image",
                video_container: "hidden mobile:absolute mobile:inset-0 mobile:z-[1] mobile:flex",
              }}
            >
              {showEmergency && (
                <div className="absolute w-full bottom-[15px] z-[2]">
                  <EmergencyDaily
                    productHandle={product.handle}
                    productType={product.productType}
                    showPreOrderFomo={showPreOrderFomo}
                    componentType={componentType}
                  />
                </div>
              )}
            </ALVideo>
          </SwiperSlide>
        )}

        {images.map((p, i) => (
          <Fragment key={p.url || i}>
            {!hasVideo && showEmergency && i === 0 && (
              <div className="absolute w-full bottom-[15px] z-[2]">
                <EmergencyDaily
                  productHandle={product.handle}
                  productType={product.productType}
                  showPreOrderFomo={showPreOrderFomo}
                  componentType={componentType}
                />
              </div>
            )}
            {showUSPImage && i === INDEX_USPS && (
              <SwiperSlide key="product_usp_mobile_pdp">
                <USPFakeImage
                  idx={i}
                  tags={product.tags}
                  aspectRatio="aspect-[0.8]"
                  titleTextSize={isMobile ? "text-[3.3vw]" : "text-[1.3vw]"}
                  uspTextSize={isMobile ? "text-[4.1vw]" : "text-[1.6vw]"}
                  componentType={componentType}
                />
              </SwiperSlide>
            )}
            <SwiperSlide
              key={`product_image_${p.url}_pdp`}
              onClick={() => onSlideClick(p)}
              data-testid="swiper-slide"
            >
              {showDesktopImage ? (
                <DesktopImage image={p} />
              ) : (
                <ALShopifyImage
                  width={isBrowser && window?.innerWidth}
                  url={p.url}
                  alt={p?.altText || ""}
                  imageClassName={`mobile:!object-cover h-full ${
                    componentType === ComponentType.CP_BYOB &&
                    product.productType === "Necklaces" &&
                    "scale-[1.5] -translate-y-[35px]"
                  }`}
                />
              )}
            </SwiperSlide>
          </Fragment>
        ))}
      </Swiper>

      {showCustomNavigationButtons && (
        <>
          {[
            {
              positionClass: "left-[20px]",
              navClass:
                componentType === ComponentType.CP_BYOB ? customBYOBPrevClass : customPDPPrevClass,
              testid: `custom-byob-prev-${product.handle}`,
              rotation: "90deg",
            },
            {
              positionClass: "right-[20px]",
              navClass:
                componentType === ComponentType.CP_BYOB ? customBYOBNextClass : customPDPNextClass,
              testid: `custom-byob-next-${product.handle}`,
              rotation: "-90deg",
            },
          ].map(({ positionClass, navClass, testid, rotation }) => (
            <button
              key={testid}
              className={cn(
                "z-[8] absolute top-1/2 -translate-y-1/2",
                positionClass,
                componentType !== ComponentType.CP_BYOB &&
                  "h-[30px] w-[30px] bg-white rounded-[4px] flex items-center justify-center",
                navClass
              )}
              type="button"
              data-testid={testid}
            >
              <IconArrow style={{ transform: `rotate(${rotation})` }} />
            </button>
          ))}
        </>
      )}
    </>
  );
}

ImagesSwiper.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({ url: PropTypes.string, altText: PropTypes.string })),
  hasVideo: PropTypes.bool,
  video: PropTypes.shape({ url: PropTypes.string }),
  componentType: PropTypes.string.isRequired,
  showEmergency: PropTypes.bool,
  product: PropTypes.shape({
    handle: PropTypes.string,
    productType: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  showPreOrderFomo: PropTypes.bool,
  onSlideClick: PropTypes.func,
  onSwiper: PropTypes.func,
  onActiveIndexChange: PropTypes.func,
  showUSPImage: PropTypes.bool,
};

export default ImagesSwiper;
